import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, Video, Loader2, Eye, EyeOff } from 'lucide-react';

const TYPES = [
  { key: 'first_login', title: 'Welcome Video (First Login)', desc: "Plays only on a user's first-ever visit to the portal." },
];

const BLOCK_PREVIEWS = [
  { key: 'coaching',    title: 'Coaching Program' },
  { key: 'workshops',   title: 'Workshops' },
  { key: 'marketplace', title: 'Marketplace' },
  { key: 'booking',     title: 'Session Booking' },
  { key: 'events',      title: 'Events (Calendar)' },
];

const VideoSlot = ({ meta, type, title, desc, onUpload, onDelete, uploading }) => {
  const inputRef = useRef(null);
  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><Video className="w-4 h-4 text-cyan-600" /> {title}</h3>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>

      {meta?.url ? (
        <div className="space-y-2">
          <video
            src={`${process.env.REACT_APP_BACKEND_URL}${meta.url}`}
            controls
            className="w-full max-w-md rounded-md border"
            style={{ maxHeight: 220 }}
            data-testid={`portal-video-preview-${type}`}
          />
          <p className="text-xs text-green-600 truncate">✓ {meta.filename || 'Uploaded'}</p>
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic">No video uploaded yet.</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        data-testid={`portal-video-input-${type}`}
        onChange={(e) => { if (e.target.files[0]) onUpload(type, e.target.files[0]); e.target.value = ''; }}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          className="btn-primary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`portal-video-upload-${type}`}
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {meta?.url ? 'Replace' : 'Upload'}
        </Button>
        {meta?.url && (
          <Button size="sm" variant="destructive" disabled={uploading} onClick={() => onDelete(type)} data-testid={`portal-video-delete-${type}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const AdminPortalVideoTab = () => {
  const [videos, setVideos] = useState({ first_login: null, returning: null });
  const [clips, setClips] = useState([]);
  const [previews, setPreviews] = useState({});
  const [uploadingType, setUploadingType] = useState(null);
  const clipInputRef = useRef(null);

  const applyRes = (data) => {
    setVideos({ first_login: data.first_login, returning: data.returning });
    setClips(data.returning_clips || []);
    setPreviews(data.block_previews || {});
  };

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/portal/admin/videos`);
      applyRes(res.data);
    } catch (e) {
      toast.error('Failed to load portal videos');
    }
  };

  useEffect(() => { fetchVideos(); }, []);

  const handleUpload = async (type, file) => {
    setUploadingType(type);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('video_type', type);
    try {
      const res = await axios.post(`${API}/portal/admin/videos`, fd);
      applyRes(res.data);
      toast.success('Video uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingType(null);
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm('Remove this video?')) return;
    try {
      const res = await axios.delete(`${API}/portal/admin/videos/${type}`);
      applyRes(res.data);
      toast.success('Video removed');
    } catch (e) {
      toast.error('Failed to remove video');
    }
  };

  const toggleClip = async (clip) => {
    const fd = new FormData();
    fd.append('enabled', !clip.enabled);
    try {
      const res = await axios.patch(`${API}/portal/admin/videos/clips/${clip.id}`, fd);
      applyRes(res.data);
      toast.success(clip.enabled ? 'Clip rested — it will not play' : 'Clip back in rotation');
    } catch (e) {
      toast.error('Could not change that clip');
    }
  };

  const deleteClip = async (clip) => {
    if (!window.confirm(`Delete "${clip.filename || 'this clip'}" for good?`)) return;
    try {
      const res = await axios.delete(`${API}/portal/admin/videos/clips/${clip.id}`);
      applyRes(res.data);
      toast.success('Clip deleted');
    } catch (e) {
      toast.error('Could not delete that clip');
    }
  };

  const activeCount = clips.filter((c) => c.enabled).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Portal Welcome Videos</CardTitle>
          <p className="text-sm text-gray-500">
            These play in the center of the members' portal landing page. Max 300MB each (MP4 recommended).
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {TYPES.map((t) => (
            <VideoSlot
              key={t.key}
              type={t.key}
              title={t.title}
              desc={t.desc}
              meta={videos[t.key]}
              onUpload={handleUpload}
              onDelete={handleDelete}
              uploading={uploadingType === t.key}
            />
          ))}
        </CardContent>
      </Card>

      <Card data-testid="portal-returning-clips-card">
        <CardHeader>
          <CardTitle>Returning Clips</CardTitle>
          <p className="text-sm text-gray-500">
            Short clips for every visit after the first. One is picked at random each time, and never
            the same one a member saw last. {activeCount === 0
              ? 'With none playing, the portal falls back to your single returning video.'
              : `${activeCount} clip${activeCount === 1 ? '' : 's'} in rotation.`}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {clips.length === 0 && (
            <p className="text-sm text-gray-400 italic" data-testid="portal-clips-empty">
              No clips yet — upload your first one below.
            </p>
          )}

          {clips.map((clip) => (
            <div
              key={clip.id}
              className={`border rounded-lg p-3 flex flex-wrap items-center gap-3 ${clip.enabled ? '' : 'bg-gray-50 opacity-70'}`}
              data-testid={`portal-clip-${clip.id}`}
            >
              <video
                src={`${process.env.REACT_APP_BACKEND_URL}${clip.url}`}
                controls
                className="rounded-md border"
                style={{ width: 220, maxHeight: 130 }}
                data-testid={`portal-clip-preview-${clip.id}`}
              />
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-medium truncate">{clip.filename || 'Clip'}</p>
                <p className={`text-xs ${clip.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                  {clip.enabled ? 'In rotation' : 'Resting — not playing'}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => toggleClip(clip)}
                data-testid={`portal-clip-toggle-${clip.id}`}
              >
                {clip.enabled ? <><EyeOff className="w-4 h-4 mr-1" /> Rest</> : <><Eye className="w-4 h-4 mr-1" /> Use</>}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => deleteClip(clip)}
                data-testid={`portal-clip-delete-${clip.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          <input
            ref={clipInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            data-testid="portal-clip-input"
            onChange={(e) => { if (e.target.files[0]) handleUpload('returning_clip', e.target.files[0]); e.target.value = ''; }}
          />
          <Button
            size="sm"
            className="btn-primary"
            disabled={uploadingType === 'returning_clip'}
            onClick={() => clipInputRef.current?.click()}
            data-testid="portal-clip-upload-btn"
          >
            {uploadingType === 'returning_clip'
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <Upload className="w-4 h-4 mr-1" />}
            Add a clip
          </Button>

          {videos.returning?.url && (
            <div className="border-t pt-3 mt-2">
              <p className="text-xs text-gray-500 mb-2">
                Your original single returning video. It plays only while no clips are in rotation.
              </p>
              <div className="flex items-center gap-3">
                <video
                  src={`${process.env.REACT_APP_BACKEND_URL}${videos.returning.url}`}
                  controls
                  className="rounded-md border"
                  style={{ width: 220, maxHeight: 130 }}
                  data-testid="portal-video-preview-returning"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete('returning')}
                  data-testid="portal-video-delete-returning"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Block Hover Previews</CardTitle>
          <p className="text-sm text-gray-500">
            Short, muted, sped-up clips that play when a member hovers each portal block. Optional — blocks show an icon if no clip is set.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {BLOCK_PREVIEWS.map((b) => (
            <VideoSlot
              key={b.key}
              type={b.key}
              title={b.title}
              desc="Hover preview clip"
              meta={previews[b.key]}
              onUpload={handleUpload}
              onDelete={handleDelete}
              uploading={uploadingType === b.key}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPortalVideoTab;
